import { useEffect, useState } from 'react';
import { ThemeProvider } from 'next-themes';
import ModernDashboard from './components/modern/ModernDashboard';
import { LoginPage } from './components/auth/LoginPage';
import { RegisterPage } from './components/auth/RegisterPage';
import HomePage from './components/home/HomePage';
import SplashScreen from './components/home/SplashScreen';
import { useAuth } from './auth/AuthContext';

const REDIRECT_KEY = 'ss.auth.redirect';

type Route =
  | { kind: 'home' }
  | { kind: 'login' }
  | { kind: 'register' }
  | { kind: 'dashboard' }
  | { kind: 'legacy'; legacyPath: string };

// readRoute parses window.location.hash into one of five buckets.
// 'dashboard' covers anything under #/dashboard (gated by auth).
// 'legacy' covers older flat hashes (e.g. #/scanner, #/configuration/...) and
// gets one-shot migrated to the new #/dashboard/<rest> shape.
function readRoute(): Route {
  const raw = window.location.hash || '';
  const stripped = raw.replace(/^#\/?/, '');
  const beforeQuery = stripped.split('?')[0];
  const first = beforeQuery.split('/')[0];

  if (first === '') return { kind: 'home' };
  if (first === 'login') return { kind: 'login' };
  if (first === 'register') return { kind: 'register' };
  if (first === 'dashboard') return { kind: 'dashboard' };
  // Legal stub routes — render the home page for now.
  if (first === 'legal') return { kind: 'home' };
  // Anything else is a stale legacy URL that should migrate to the new prefix.
  // Preserve the full path + query so e.g. `#/trade-viewer?scenario=abc`
  // becomes `#/dashboard/trade-viewer?scenario=abc`.
  return { kind: 'legacy', legacyPath: stripped };
}

function AppRouter() {
  const { isAuthenticated, isLoading } = useAuth();
  const [route, setRoute] = useState<Route>(() => readRoute());

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Wait for auth hydration before deciding — otherwise we'd flash the
  // login redirect for users who actually have a valid session.
  if (isLoading) {
    return <SplashScreen />;
  }

  // Public auth forms — anyone can hit them.
  if (route.kind === 'login') return <LoginPage />;
  if (route.kind === 'register') return <RegisterPage />;

  // Marketing home is public — signed-in users see it too, just with a
  // "Go to dashboard" CTA in place of the sign-in buttons.
  if (route.kind === 'home') {
    return <HomePage />;
  }

  // One-shot migration of legacy flat hashes (e.g. #/scanner) to the new
  // #/dashboard/<rest> shape. Existing share links don't matter (no public
  // shares yet), so we just bump anyone who shows up at an old URL.
  if (route.kind === 'legacy') {
    window.location.hash = `#/dashboard/${route.legacyPath}`;
    return null;
  }

  // route.kind === 'dashboard' — gate behind auth.
  if (!isAuthenticated) {
    try {
      // Save the requested hash so LoginPage can bounce the user back
      // after a successful sign-in (LoginPage already reads this key).
      localStorage.setItem(REDIRECT_KEY, window.location.hash);
    } catch {
      /* swallow */
    }
    window.location.hash = '#/login';
    return null;
  }
  return <ModernDashboard />;
}

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={true}
      disableTransitionOnChange={false}
    >
      <AppRouter />
    </ThemeProvider>
  );
}

export default App;
